<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  // 助手 ID
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const searchQuery = ref('');
const processingIds = ref(new Set()); // 正在处理的帮助中心 ID
const batchSelectedIds = ref(new Set()); // 批量选中的帮助中心 ID
const isBatchSubmitting = ref(false);
const hoveredPortalId = ref(null); // hover 状态的帮助中心 ID

// 帮助中心列表（从 portals store 获取）
const allPortals = computed(() => {
  const byId = store.state.portals?.portals?.byId || {};
  return Object.values(byId);
});

const uiFlags = computed(() => store.state.portals?.uiFlags || {});
const isLoadingPortals = computed(() => uiFlags.value.isFetching);

// 从 store 获取已关联的帮助中心列表
const assistantPortals = useMapGetter('pilotAssistantPortals/getRecords');

// 已关联的帮助中心 ID 集合
const linkedIdsSet = computed(() => {
  const ids = (assistantPortals.value || []).map(portal => portal.id);
  return new Set(ids);
});

// 根据搜索显示的帮助中心列表
const displayedPortals = computed(() => {
  let list = allPortals.value;

  // 搜索过滤
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    list = list.filter(portal => {
      const name = (portal.name || '').toLowerCase();
      const headerText = (portal.header_text || '').toLowerCase();
      return name.includes(query) || headerText.includes(query);
    });
  }

  return list;
});

// 加载帮助中心列表
const fetchPortals = async () => {
  await store.dispatch('portals/index');
};

// 刷新已关联的帮助中心列表
const refreshLinkedPortals = async () => {
  await store.dispatch('pilotAssistantPortals/get', {
    assistantId: props.assistantId,
  });
};

// 判断帮助中心是否已关联
const isLinked = portalId => linkedIdsSet.value.has(portalId);

// 判断帮助中心是否正在处理中
const isProcessing = portalId => processingIds.value.has(portalId);

// 批量选择相关
const isSelected = portalId => batchSelectedIds.value.has(portalId);

const toggleSelection = portalId => {
  if (batchSelectedIds.value.has(portalId)) {
    batchSelectedIds.value.delete(portalId);
  } else {
    batchSelectedIds.value.add(portalId);
  }
  batchSelectedIds.value = new Set(batchSelectedIds.value);
};

const hasSelection = computed(() => batchSelectedIds.value.size > 0);

// 全选/取消全选标签
const buildSelectedCountLabel = computed(() => {
  const count = displayedPortals.value.length;
  const isAllSelected = batchSelectedIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedCountLabel = computed(() => {
  return `已选择 ${batchSelectedIds.value.size} 项`;
});

// 全选/取消全选
const toggleSelectAll = () => {
  const allItems = displayedPortals.value;
  const allSelected =
    batchSelectedIds.value.size === allItems.length && allItems.length > 0;

  if (allSelected) {
    batchSelectedIds.value = new Set();
  } else {
    batchSelectedIds.value = new Set(allItems.map(portal => portal.id));
  }
};

// 获取帮助中心的文章数量
const getArticlesCount = portal => {
  if (portal.articles_count !== undefined && portal.articles_count !== null) {
    return portal.articles_count;
  }
  if (portal.meta?.all_articles_count !== undefined) {
    return portal.meta.all_articles_count;
  }
  return 0;
};

// 获取帮助中心的 Logo URL
const getLogoUrl = portal => {
  if (portal.logo_url) {
    return portal.logo_url;
  }
  if (portal.logo?.file_url) {
    return portal.logo.file_url;
  }
  return null;
};

// 切换关联状态
const toggleLink = async (portal, action) => {
  processingIds.value.add(portal.id);
  processingIds.value = new Set(processingIds.value);

  try {
    const dispatchArgs = { assistantId: props.assistantId };
    if (action === 'add') {
      dispatchArgs.portalId = portal.id;
      await store.dispatch('pilotAssistantPortals/create', dispatchArgs);
      useAlert(t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.ADD.SUCCESS'));
    } else {
      dispatchArgs.id = portal.id;
      await store.dispatch('pilotAssistantPortals/delete', dispatchArgs);
      useAlert(t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.DELETE.SUCCESS'));
    }
    await refreshLinkedPortals();
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.OPERATION_ERROR')
    );
  } finally {
    processingIds.value.delete(portal.id);
    processingIds.value = new Set(processingIds.value);
  }
};

// 批量操作
const handleBatchOperation = async action => {
  if (!hasSelection.value || isBatchSubmitting.value) return;

  isBatchSubmitting.value = true;
  try {
    let ids = Array.from(batchSelectedIds.value);

    // 添加时过滤掉已关联的帮助中心
    if (action === 'add') {
      ids = ids.filter(id => !linkedIdsSet.value.has(id));
    }
    // 移除时过滤掉未关联的帮助中心
    if (action === 'remove') {
      ids = ids.filter(id => linkedIdsSet.value.has(id));
    }

    if (ids.length === 0) {
      batchSelectedIds.value = new Set();
      return;
    }

    const dispatchAction =
      action === 'add'
        ? 'pilotAssistantPortals/create'
        : 'pilotAssistantPortals/delete';

    await Promise.all(
      ids.map(portalId => {
        const args = { assistantId: props.assistantId };
        if (action === 'add') {
          args.portalId = portalId;
        } else {
          args.id = portalId;
        }
        return store.dispatch(dispatchAction, args);
      })
    );
    await refreshLinkedPortals();
    batchSelectedIds.value = new Set();
    if (action === 'add') {
      useAlert(t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.ADD.SUCCESS'));
    } else {
      useAlert(t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.DELETE.SUCCESS'));
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.OPERATION_ERROR')
    );
  } finally {
    isBatchSubmitting.value = false;
  }
};

// 关闭弹窗
const handleClose = () => {
  emit('close');
};

// 主动关闭弹窗
const closeDialog = () => {
  dialogRef.value?.close();
};

// 打开弹窗
const open = async () => {
  searchQuery.value = '';
  batchSelectedIds.value = new Set();
  dialogRef.value?.open();
  // 先刷新已关联的帮助中心列表，确保数据是最新的
  await refreshLinkedPortals();
  fetchPortals();
};

// 搜索防抖
const debouncedSearch = useDebounceFn(() => {
  // 搜索是本地的，不需要重新请求
}, 300);

watch(searchQuery, () => {
  debouncedSearch();
});

defineExpose({
  dialogRef,
  open,
  close: handleClose,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="xl"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <!-- 搜索工具条 - 无选中时显示 -->
      <div v-if="!hasSelection" class="flex items-center gap-2">
        <Input
          v-model="searchQuery"
          type="search"
          size="sm"
          :placeholder="
            t(
              'PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.SEARCH_PLACEHOLDER'
            )
          "
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="flex-1"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
      </div>

      <!-- 批量操作工具条 - 有选中时显示 -->
      <div v-else class="flex items-center gap-3 px-3">
        <div class="flex items-center gap-2">
          <Checkbox
            :model-value="
              batchSelectedIds.size === displayedPortals.length &&
              displayedPortals.length > 0
            "
            :indeterminate="
              batchSelectedIds.size > 0 &&
              batchSelectedIds.size < displayedPortals.length
            "
            @update:model-value="toggleSelectAll"
          />
          <span class="text-sm font-medium text-n-slate-12 tabular-nums">
            {{ buildSelectedCountLabel }}
          </span>
        </div>
        <span class="text-sm text-n-slate-10 truncate tabular-nums">
          {{ selectedCountLabel }}
        </span>
        <div class="h-4 w-px bg-n-strong" />
        <Button
          label="批量添加"
          size="sm"
          variant="ghost"
          color="slate"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('add')"
        />
        <Button
          label="批量移除"
          size="sm"
          variant="ghost"
          color="ruby"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('remove')"
        />
      </div>

      <!-- 帮助中心列表 -->
      <div class="flex flex-col gap-2 max-h-96 overflow-y-auto">
        <!-- 加载中 -->
        <div v-if="isLoadingPortals" class="flex justify-center py-8">
          <Spinner :size="20" />
        </div>

        <!-- 空状态 -->
        <div
          v-else-if="displayedPortals.length === 0"
          class="text-sm text-n-slate-9 text-center py-8"
        >
          {{
            searchQuery
              ? t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.EMPTY_SEARCH')
              : t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.EMPTY_LIST')
          }}
        </div>

        <!-- 帮助中心列表 -->
        <template v-else>
          <div
            v-for="portal in displayedPortals"
            :key="portal.id"
            class="flex items-center gap-3 p-3 rounded-lg border transition-colors relative"
            :class="[
              isSelected(portal.id)
                ? 'border-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                : 'border-n-slate-6 hover:border-n-slate-8 hover:bg-n-slate-1 dark:hover:bg-n-slate-800',
            ]"
            @mouseenter="hoveredPortalId = portal.id"
            @mouseleave="hoveredPortalId = null"
          >
            <!-- Checkbox - hover或有选中时才显示 -->
            <div
              :class="{
                invisible:
                  hoveredPortalId !== portal.id && batchSelectedIds.size === 0,
              }"
            >
              <Checkbox
                :model-value="isSelected(portal.id)"
                @update:model-value="toggleSelection(portal.id)"
                @click.stop
              />
            </div>

            <!-- 帮助中心 Logo -->
            <div
              class="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
              :class="getLogoUrl(portal) ? '' : 'bg-n-brand/10'"
            >
              <img
                v-if="getLogoUrl(portal)"
                :src="getLogoUrl(portal)"
                :alt="portal.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-sm font-semibold text-n-brand">
                {{ portal.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>

            <!-- 帮助中心信息 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-12 truncate">
                  {{ portal.name }}
                </span>
                <!-- 已关联标记 -->
                <span
                  v-if="isLinked(portal.id)"
                  class="px-1.5 py-0.5 text-xs font-medium text-white whitespace-nowrap rounded bg-n-ruby-9"
                >
                  已关联
                </span>
              </div>
              <div class="text-xs text-n-slate-10 mt-0.5 truncate">
                {{
                  portal.header_text ||
                  t(
                    'PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.NO_DESCRIPTION'
                  )
                }}
              </div>
            </div>

            <!-- 文章数量 -->
            <div
              class="flex items-center gap-2 flex-shrink-0 text-xs text-n-slate-9"
            >
              <div class="flex items-center gap-1">
                <span class="i-lucide-file-text text-sm" />
                <span>{{ getArticlesCount(portal) }}</span>
              </div>
            </div>

            <!-- 添加/移除按钮 -->
            <Button
              v-if="isLinked(portal.id)"
              icon="i-lucide-minus"
              size="sm"
              variant="faded"
              color="ruby"
              :is-loading="isProcessing(portal.id)"
              :disabled="isProcessing(portal.id)"
              @click="toggleLink(portal, 'remove')"
            />
            <Button
              v-else
              icon="i-lucide-plus"
              size="sm"
              variant="faded"
              color="slate"
              :is-loading="isProcessing(portal.id)"
              :disabled="isProcessing(portal.id)"
              @click="toggleLink(portal, 'add')"
            />
          </div>
        </template>
      </div>

      <!-- 底部按钮 -->
      <div
        class="flex items-center justify-end gap-2 pt-4 border-t border-n-slate-6"
      >
        <Button
          :label="t('PILOT.ASSISTANTS.CONFIG.HELP_CENTERS.SELECTOR.CLOSE')"
          variant="faded"
          color="slate"
          @click="closeDialog"
        />
      </div>
    </div>
  </Dialog>
</template>
